import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Search, Check } from "lucide-react";
import { setTranslateLang, getTranslateLang, initTranslate } from "../lib/googleTranslate";
import SpinningGlobe from "./SpinningGlobe";

// Must stay in sync with includedLanguages in public/index.html
const LANGUAGES = [
  { code: "en", flag: "🇺🇸", name: "English", en: "English" },
  { code: "es", flag: "🇪🇸", name: "Español", en: "Spanish" },
  { code: "vi", flag: "🇻🇳", name: "Tiếng Việt", en: "Vietnamese" },
  { code: "zh-CN", flag: "🇨🇳", name: "中文", en: "Chinese Mandarin" },
  { code: "ar", flag: "🇸🇦", name: "العربية", en: "Arabic" },
  { code: "so", flag: "🇸🇴", name: "Soomaali", en: "Somali" },
  { code: "ko", flag: "🇰🇷", name: "한국어", en: "Korean" },
  { code: "fr", flag: "🇫🇷", name: "Français", en: "French" },
  { code: "de", flag: "🇩🇪", name: "Deutsch", en: "German" },
  { code: "pt", flag: "🇧🇷", name: "Português", en: "Portuguese" },
  { code: "ja", flag: "🇯🇵", name: "日本語", en: "Japanese" },
  { code: "tl", flag: "🇵🇭", name: "Tagalog", en: "Tagalog Filipino" },
  { code: "hi", flag: "🇮🇳", name: "हिन्दी", en: "Hindi" },
  { code: "my", flag: "🇲🇲", name: "မြန်မာ", en: "Burmese Myanmar" },
  { code: "ru", flag: "🇷🇺", name: "Русский", en: "Russian" },
  { code: "ht", flag: "🇭🇹", name: "Kreyòl Ayisyen", en: "Haitian Creole" },
  { code: "fa", flag: "🇮🇷", name: "فارسی", en: "Persian Farsi" },
  { code: "ur", flag: "🇵🇰", name: "اردو", en: "Urdu" },
  { code: "am", flag: "🇪🇹", name: "አማርኛ", en: "Amharic" },
];

export const LanguageMenu = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [currentCode, setCurrentCode] = useState(getTranslateLang());
  const ref = useRef(null);

  const current = LANGUAGES.find((l) => l.code === currentCode) || LANGUAGES[0];

  // Re-apply the saved language on load (fixes cases where the cookie was dropped
  // inside the Emergent iframe / on iOS Safari).
  useEffect(() => {
    initTranslate();
    const openMenu = () => setOpen(true);
    window.addEventListener("open-language-menu", openMenu);
    return () => window.removeEventListener("open-language-menu", openMenu);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? LANGUAGES.filter((l) => l.name.toLowerCase().includes(q) || l.en.toLowerCase().includes(q) || l.code.includes(q))
    : LANGUAGES;

  const select = (code) => {
    setOpen(false);
    setCurrentCode(code);
    setTranslateLang(code); // translates in place (no reload) for non-English
  };

  return (
    <div className="relative notranslate" translate="no" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 sm:gap-2 px-1 transition-all text-xs flex-shrink-0 ${
          currentCode !== "en" ? "text-blue-300 font-bold" : "text-white"
        }`}
        data-testid="language-menu-trigger"
        aria-label="Select language"
        aria-expanded={open}
      >
        <span className="globe-footer rounded-full flex-shrink-0 hidden sm:flex">
          <SpinningGlobe size="w-[4.5rem] h-[4.5rem] sm:w-[5.5rem] sm:h-[5.5rem]" alt="" />
        </span>
        <span className="text-base sm:text-lg leading-none">{current.flag}</span>
        <span className="hidden sm:inline font-medium text-sm max-w-[92px] truncate">{current.name}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl border border-slate-200 shadow-2xl overflow-hidden z-[70]"
          data-testid="language-menu-dropdown"
        >
          <div className="p-2.5 border-b border-slate-100">
            <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-slate-100">
              <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search language…"
                className="bg-transparent text-sm text-slate-700 outline-none w-full placeholder:text-slate-400"
                data-testid="language-search"
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {filtered.map((l) => {
              const active = l.code === currentCode;
              return (
                <button
                  key={l.code}
                  onClick={() => select(l.code)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
                    active ? "bg-blue-50 text-primary font-semibold" : "text-slate-700 hover:bg-slate-50"
                  }`}
                  data-testid={`language-option-${l.code}`}
                >
                  <span className="text-lg leading-none">{l.flag}</span>
                  <span className="flex-1">{l.name}</span>
                  {active && <Check className="w-4 h-4 text-primary" />}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="px-4 py-6 text-center text-sm text-slate-400">No match</p>
            )}
          </div>
          <div className="px-4 py-2 text-[11px] text-slate-400 border-t border-slate-100 bg-slate-50">
            {LANGUAGES.length} languages · powered by Google Translate
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageMenu;
